/**
 * Utility functions for calculating initial map position and bounds
 */

import { LatLngBounds, type LatLngExpression } from 'leaflet';
import { DEFAULT_MAP_CENTER } from './map-constants';

export interface InitialMapPosition {
    initialCenter: LatLngExpression;
    initialBounds: LatLngBounds | null;
}

/**
 * Calculate initial map position - prioritize high-importance places
 * 
 * Strategy:
 * - Bounds: Use all entities to ensure everything is visible
 * - Center: Prioritize high-importance places
 */
export function calculateInitialMapPosition(placesByPassage: PlacesByPassage | null): InitialMapPosition {
    if (!placesByPassage || placesByPassage.entities.length === 0) {
        return { initialCenter: DEFAULT_MAP_CENTER, initialBounds: null };
    }

    const { entities } = placesByPassage;
    
    // Get high-importance places for centering strategy
    const highImportance = entities.filter(e => e.importance === 'high');
    
    // Strategy for bounds: Use all entities to ensure everything is visible
    // Strategy for center: Prioritize high-importance places
    const bounds = new LatLngBounds(
        entities.map(e => [e.coordinates.latitude, e.coordinates.longitude] as LatLngExpression)
    );
    
    // Determine center based on importance
    let center: LatLngExpression;
    if (highImportance.length === 1) {
        // Single high-importance place - center on it
        center = [highImportance[0].coordinates.latitude, highImportance[0].coordinates.longitude];
    } else if (highImportance.length > 1) {
        // Multiple high-importance places - center on their midpoint
        const highBounds = new LatLngBounds(
            highImportance.map(e => [e.coordinates.latitude, e.coordinates.longitude] as LatLngExpression)
        );
        center = highBounds.getCenter();
    } else {
        // No high-importance places - use geographic center of all places
        center = bounds.getCenter();
    }

    return { initialCenter: center, initialBounds: bounds };
}

